
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Function to get the latest month
export const getLatestMonth = async (): Promise<number> => {
  try {
    const { data, error } = await supabase
      .from('issue')
      .select('value')
      .eq('key', 'latest_month')
      .maybeSingle();
    
    if (error) {
      console.error("Error fetching latest month:", error);
      return 1; // Default fallback to January
    }
    
    if (!data) {
      console.error("No latest_month found in issue table");
      return 1; // Default fallback
    }
    
    // Parse the month value - handle number, string, or JSON
    const value = data.value;
    if (typeof value === 'number') {
      return value;
    } else if (typeof value === 'string') {
      const parsed = parseInt(value.replace(/^"|"$/g, ''));
      if (!isNaN(parsed)) return parsed;
    }
    
    console.error("Could not parse latest month value:", value);
    return 1; // Default fallback
  } catch (error) {
    console.error("Error in getLatestMonth:", error);
    return 1; // Default fallback
  }
};

// Function to get the latest year
export const getLatestYear = async (): Promise<number> => {
  try {
    const { data, error } = await supabase
      .from('issue')
      .select('value')
      .eq('key', 'latest_year')
      .maybeSingle();
    
    if (error) {
      console.error("Error fetching latest year:", error);
      return 2026; // Default fallback
    }
    
    if (!data) {
      console.error("No latest_year found in issue table");
      return 2026; // Default fallback
    }
    
    // Parse the year value - handle number, string, or JSON
    const value = data.value;
    if (typeof value === 'number') {
      return value;
    } else if (typeof value === 'string') {
      const parsed = parseInt(value.replace(/^"|"$/g, ''));
      if (!isNaN(parsed)) return parsed;
    }
    
    console.error("Could not parse latest year value:", value);
    return 2026; // Default fallback
  } catch (error) {
    console.error("Error in getLatestYear:", error);
    return 2026; // Default fallback
  }
};

// Function to get the latest issue text
export const getLatestIssue = async (): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('issue')
      .select('value')
      .eq('key', 'latest_issue')
      .maybeSingle();
    
    if (error) {
      console.error("Error fetching latest issue:", error);
      return "January 2026"; // Default fallback
    }
    
    if (!data) {
      console.error("No latest_issue found in issue table");
      return "January 2026"; // Default fallback
    }
    
    // Parse the issue value - handle string or JSON
    const value = data.value;
    if (typeof value === 'string') {
      return value.replace(/^"|"$/g, '');
    }
    
    console.error("Could not parse latest issue value:", value);
    return "January 2026"; // Default fallback
  } catch (error) {
    console.error("Error in getLatestIssue:", error);
    return "January 2026"; // Default fallback
  }
};

// Function to update the latest month and year 
// (simplified since the trigger will handle updating latest_issue)
export const updateLatestIssue = async (month: number, year: number): Promise<boolean> => {
  try {
    // Update latest_month
  const { error: monthError } = await supabase
      .from('issue')
      .update({ value: month })
      .eq('key', 'latest_month');
    
    if (monthError) {
      console.error("Error updating latest month:", monthError);
      return false;
    }
    
    // Update latest_year
  const { error: yearError } = await supabase
      .from('issue')
      .update({ value: year })
      .eq('key', 'latest_year');
    
    if (yearError) {
      console.error("Error updating latest year:", yearError);
      return false;
    }
    
    // We don't need to update latest_issue manually anymore
    // as the database trigger will handle that automatically
    
    toast.success("Latest issue updated successfully");
    return true;
  } catch (error) {
    console.error("Error updating latest issue values:", error);
    toast.error("Failed to update latest issue");
    return false;
  }
};
